import { Card, CardContent, Container, Grid, Typography, Link } from '@material-ui/core';
import { useRouter } from 'next/router';
import React, { useContext } from 'react';
import * as Yup from 'yup';
import { Button, ButtonProps, Form, FormikValues, FormWrap } from '../formikFormInputs';
import { Agreement, Banner, bannerStyles, LoginFields } from './Login';
import { useLogin } from '../../lib/useLogin';
import { AGENT_SIGNING_KEY, AUTH_SESSION_KEY, initFalse } from '../../utils/constants';
import { save } from '../../utils/storage';
import { refreshAccessToken } from '../../lib/privateApi';
import {
  checkSellerAgreementSignValid,
  handleFormError,
  redirectIfPreferred,
  stringify,
  verifyLastSession
} from '../../utils/commonUtils';
import { SnackbarContext } from '../Snackbar/Context';
import { Paths, ROLES, SnackBarSeverity } from '../../utils/enums';
import { useAuth } from '../../lib/useAuth';
import { REQUIRED } from '../../utils/validation';
import { fetcher } from '../Auth/AuthProvider';


interface AdvisorResourcesProps {
  preferredText: any;
  preferredTitle: any;
  registerText: any;
  registerTitle: any;
  subTitle: any;
  title: any;
}


const AdvisorResourcesContent: React.FC<AdvisorResourcesProps> = ({subTitle, title}) => {
  const classes = bannerStyles();
  const {logout, mutateUser, user} = useAuth();
  const router = useRouter();
  const snackbar = useContext(SnackbarContext);
  const [showConfirmation, setShowConfirmation] = React.useState(initFalse);
  const onCancel = async () => {
    await logout()
    setShowConfirmation(initFalse);
  };
  const onConfirm = async () => {
    setShowConfirmation(initFalse);
    return router.push(Paths.AgentPortal);
  };
  const log = (a: string) => {
    snackbar.showMessage(a, SnackBarSeverity.Error);
    logout();
  };
  const onSubmit = async ({email, password}: FormikValues, {resetForm}: FormikValues) => {
    try {
      const {accessToken, user} = await useLogin({email, password, role: ROLES.Agent});

      if (redirectIfPreferred(accessToken, user)) {
        snackbar.showMessage('Redirecting to Ilia...', SnackBarSeverity.Success);
        return;
      }

      await save(AUTH_SESSION_KEY, accessToken);
      await mutateUser(user);

      refreshAccessToken(log);

      const alreadySigned = checkSellerAgreementSignValid(user.agent.sellerAgreements);
      if (alreadySigned) {
        return router.push(Paths.AgentPortal);
      }

      await save(AGENT_SIGNING_KEY, stringify(alreadySigned));
      setShowConfirmation(!initFalse);

      resetForm();
      // @ts-ignore TS1196: Catch clause variable cannot have a type annotation.
    } catch (err: any) {
      const errMessage = handleFormError(err);
      snackbar.showMessage(errMessage, SnackBarSeverity.Error);
    }
  };
  const _validationSchema = Yup.object({
    email: Yup.string().email().required(REQUIRED),
    password: Yup.string().required(REQUIRED)
  });
  const _formikProps = {
    children: ({isSubmitting, ..._formInternalProps}: FormikValues) => (
      <Form className="labelShrink mt20">
        <LoginFields {..._formInternalProps} />
          <div className="mt30 textCenter" >
            <Button disabled={isSubmitting} loading={isSubmitting} {...loginButtonProps} />
            <Link href="/agent-resources/change-password" className="textCenter floatLeft w100 mt15 mb20">Forgot Password?</Link>
          </div>
      </Form>
    ),
    initialValues: {email: '', password: ''},
    onSubmit,
    validationSchema: _validationSchema
  };
  const loginButtonProps: ButtonProps = {
    color: 'primary',
    label: 'Login',
    size: 'medium',
    type: 'submit',
    variant: 'contained'
  };
  const IlliLogo: React.FC = () => (
    <img
      style={{width:'85%'}}
      alt="ilia logo"
      src="data:image/png;base64,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"
    />
  );

  React.useEffect(() => {
    fetcher().then(r => verifyLastSession(router, user || r));
  }, [user]);

  return (
    <>
      {
        showConfirmation &&
        <Agreement onCancel={onCancel} onConfirm={onConfirm} show={showConfirmation}/>
      }
      <Banner title={title} subTitle={subTitle}/>
      <div className="sectionPD">
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={5}>
                <Grid item xs={12} md={12}>
                  <Card className="mt20">
                    <CardContent>
                      <Grid container>
                        <Grid item lg={6}>
                          <Typography variant="subtitle1" component="strong" color="primary" className="mt20 f28 ml10 floatLeft">Agent Login</Typography>
                        </Grid>
                        <Grid item lg={6}>
                          <IlliLogo />
                        </Grid>
                      </Grid>
                      <FormWrap {..._formikProps} />
                    </CardContent>
                  </Card>
                  <Card className={classes.register}>
                    <CardContent className={classes.textWhite + " textCenter "}>
                      <Typography variant="subtitle1" className="mb10 floatLeft w100">Become a registered agent here</Typography>
                      <Link href="/agent-resources/register">
                        <Button size="small" className="mt20" variant="outlined" color="secondary">
                          Go to Registration
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </Grid>
            </Grid>
          </Grid>
        </Container>
      </div>
    </>
  );
};

export default AdvisorResourcesContent;
